import { Divider } from '@/components/divider';
import { AudienceConnectorsLinkMessage } from '@/features/audiences';
import { useWorkspace } from '@/features/workspaces/hooks';
import { DistributionChannelConnector } from '@/models/distributionChannels';
import { useCreateCampaignMutation } from '@/services/endpoints/campaigns';
import { usePatchDistributionChannelMutation, useUpdateDistributionChannelFieldsMutation } from '@/services/endpoints/distributionChannels';
import { isLastItemArray } from '@/utils/isLastItemArray';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CreateCampaignFormInput } from '../../../../validation';
import { CampaignStepHeader, CampaignStepTitle } from '../../../common';
import { DestinationItemData } from './components';
import { CampaignContent, CampaignTraitStepItem } from './components/types';
import { IBaseAudienceStepProps } from './types';

export const SelectTraitsStep: FC<IBaseAudienceStepProps> = ({ toNextStep, toPreviousStep }) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext<CreateCampaignFormInput>();
  const workspace = useWorkspace();
  const [campaignConnectors, campaignAudience] = getValues(['connectors', 'audience']);

  const [updateDistributionChannelFields, { isLoading: isUpdateFieldsLoading }] = useUpdateDistributionChannelFieldsMutation();

  const content = useMemo<CampaignContent[]>(
    () =>
      campaignConnectors.map(({ connector, distributionChannelContent }) => ({
        connector,
        distributionChannelContent,
      })),
    [campaignConnectors]
  );

  const [data, setData] = useState<CampaignTraitStepItem[]>(
    content.map((data) => ({
      ...data,
      selectedFields: data.distributionChannelContent.integrationsResult.cyclr?.action.fields.reduce(
        (prev, field) => ({
          ...prev,
          [field.fieldId]: true,
        }),
        {}
      ),
    }))
  );

  const handleSetField = (connector: DistributionChannelConnector, fieldId: number, checked: boolean) => {
    setData((prev) => {
      const newData = prev.map((oldItem) => {
        if (oldItem.connector.name === connector.name) {
          return {
            ...oldItem,
            selectedFields: { ...oldItem.selectedFields, [fieldId]: checked },
            isUpdateSuccess: false,
          };
        } else {
          return oldItem;
        }
      });

      return newData;
    });
  };

  const handleSetIsUpdateSuccessful = (connector: DistributionChannelConnector) => {
    setData((prev) => {
      const newData = prev.map((oldItem) => {
        if (oldItem.connector.name === connector.name) {
          return {
            ...oldItem,
            isUpdateSuccess: true,
          };
        } else {
          return oldItem;
        }
      });

      return newData;
    });
  };

  const handleLaunchCampaign = async () => {
    const dataToUpdate = data.filter((item) => !item.isUpdateSuccess);
    for (let i = 0; i < dataToUpdate.length; i++) {
      const item = dataToUpdate[i];
      const fieldsToIgnore = item.distributionChannelContent.integrationsResult.cyclr.action.fields
        .filter((field) => !item.selectedFields[field.fieldId])
        .map((field) => ({
          stepId: field.stepId,
          fieldId: field.fieldId,
          mappingType: 'Ignore',
          value: field.value,
        }));
      if (fieldsToIgnore.length === 0) {
        handleSetIsUpdateSuccessful(item.connector);
        return;
      }
      const result = await updateDistributionChannelFields({
        workspaceId: workspace.id,
        audienceId: item.distributionChannelContent.distributionChannel.audienceId,
        distributionChannelId: item.distributionChannelContent.distributionChannel.id,
        fields: fieldsToIgnore,
      });

      if ('data' in result) {
        handleSetIsUpdateSuccessful(item.connector);
      }
    }
  };

  const [patchDistributionChannel, { isLoading: isPatchDistributionChannelLoading }] = usePatchDistributionChannelMutation();
  const [createCampaign, { isSuccess: isCampaignCreated }] = useCreateCampaignMutation();
  const [isCampaignLaunching, setIsCampaignLaunching] = useState(false);

  const syncEvents = async () => {
    try {
      setIsCampaignLaunching(true);
      await Promise.all(
        data.map(async (data) => {
          await patchDistributionChannel({
            workspaceId: workspace.id,
            audienceId: data.distributionChannelContent.distributionChannel.audienceId,
            distributionChannelId: data.distributionChannelContent.distributionChannel.id,
            parameters: {
              activate: true,
              allowAddUsers: true,
              allowRemoveUsers: true,
              allowUpdateUsers: true,
            },
          });
        })
      );
    } catch (error) {
      setIsCampaignLaunching(false);
    }
  };

  useEffect(() => {
    try {
      if (data.every((item) => item.isUpdateSuccess)) {
        syncEvents().then(() => {
          const [campaignName, campaignDescription, campaignCreatedDataSources] = getValues(['name', 'description', 'createdDataSources']);

          setIsCampaignLaunching(true);
          const createdDataSource = campaignCreatedDataSources.find((ds) => campaignAudience.datasourceIds.includes(ds.id));

          createCampaign({
            workspaceId: workspace.id,
            name: campaignName,
            description: campaignDescription,
            audienceId: campaignAudience.id,
            destinationTypes: data.map((c) => c.connector.name),
            datasourceTypes: createdDataSource ? [createdDataSource.type.id] : [],
          });
        });
      }
    } finally {
      setIsCampaignLaunching(false);
    }
  }, [data]);

  useEffect(() => {
    if (isCampaignCreated) {
      toNextStep();
    }
  }, [isCampaignCreated]);

  const disableOrLoadingButton = isCampaignLaunching || isUpdateFieldsLoading || isPatchDistributionChannelLoading;

  return (
    <div className='flex flex-col gap-12'>
      <CampaignStepHeader
        primaryButton={{
          text: t('campaign:confirmAndCreate'),
          onClick: handleLaunchCampaign,
          isLoading: disableOrLoadingButton,
          disabled: disableOrLoadingButton,
        }}
        secondaryButton={{
          text: t('COMMON:goBack'),
          onClick: toPreviousStep,
          disabled: disableOrLoadingButton,
        }}
      >
        <CampaignStepTitle
          classNames={{ title: 'first-line:text-primary-900' }}
          title='whichCharacteristicsYouWantToTransfer_title'
          description='whichCharacteristicsYouWantToTransfer_description'
        />
      </CampaignStepHeader>
      <div className='flex max-w-[1124px] flex-col gap-12'>
        <AudienceConnectorsLinkMessage audienceName={campaignAudience.name} connectors={content} />
        <div className='flex grow flex-col gap-8 p-6 pl-0'>
          {data.map((data, index, arr) => (
            <Fragment key={data.connector.name}>
              <DestinationItemData key={data.connector.name} data={data} setField={handleSetField} />

              {!isLastItemArray(index, arr) && <Divider />}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};
